import { TextCell } from '../DisplayCells'
import { ReactNode } from 'react'
import { styled } from '@mui/material'
import { CaretDownIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { Dropdown } from 'ComponentLibrary/src/piazza/forms/Dropdown/Dropdown'

type SelectCellOption = {
    value: string | number
    label: string
    disabled?: boolean
}

type SelectCellProps = {
    options: SelectCellOption[]
    updateCell: (newValue: string | number) => void
    displayCell?: ReactNode
    renderOption?: (value: string | number) => ReactNode
    selectProps?: {
        renderValue?: (value: string | number) => ReactNode
    }
    disabled?: boolean
    value: string | number
}

export function EditableSelectCell({ value, options, updateCell, displayCell, renderOption, disabled, selectProps = {} }: SelectCellProps) {
    const selectedOptionLabel = options.find((option) => option.value === value)?.label
    const cell = displayCell || selectProps.renderValue?.(value) || <TextCell maxWidth="200px" text={selectedOptionLabel ?? ''} />

    return (
        <SelectCellRoot $disabled={disabled}>
            <div className="cell-value">{cell}</div>
            {!disabled && <CaretDownIcon size={tokens.SizingIconS} color={tokens.ColorIconSubtle} weight="bold" aria-hidden />}
            <Dropdown
                className="cell-dropdown"
                options={options}
                value={value}
                renderOption={renderOption ? (option) => renderOption(option.value) : undefined}
                onValueChange={(newValue) => {
                    if (newValue !== undefined) {
                        updateCell(newValue)
                    }
                }}
                openOnFocus
                disabled={disabled}
            />
        </SelectCellRoot>
    )
}

const SelectCellRoot = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $disabled?: boolean }>(({ $disabled }) => ({
    alignItems: 'center',
    borderRadius: tokens.SizingCornerS,
    boxSizing: 'content-box',
    display: 'flex',
    gap: tokens.SizingSpacing100,
    height: '100%',
    marginBlock: `calc(-1 * var(--table-cell-padding-block, ${tokens.SizingSpacing100}))`,
    marginInline: `calc(-1 * var(--table-cell-padding-inline, ${tokens.SizingSpacing200}))`,
    opacity: $disabled ? 0.75 : undefined,
    padding: `var(--table-cell-padding-block, ${tokens.SizingSpacing100}) var(--table-cell-padding-inline, ${tokens.SizingSpacing200})`,
    pointerEvents: $disabled ? 'none' : undefined,
    position: 'relative',
    width: '100%',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: tokens.SizingSpacing050,
        borderRadius: 'inherit',
        pointerEvents: 'none',
        border: `${tokens.SizingBorderS} solid transparent`,
        zIndex: 1,
    },
    '&:hover': {
        cursor: 'pointer',
    },
    '&:focus-within::after': {
        borderColor: tokens.ColorBorderFocused,
    },
    '& > .cell-value': {
        minWidth: 0,
        textAlign: 'start',
        width: '100%',
    },
    '& > .cell-value div': {
        boxSizing: 'border-box',
        maxWidth: '100%',
    },
    '& > svg': {
        display: 'none',
        flexShrink: 0,
    },
    '&:hover > svg': {
        display: 'block',
    },
    '& > .cell-dropdown': {
        position: 'absolute',
        inset: 0,
        minHeight: 0,
        opacity: 0,
    },
    '& .MuiOutlinedInput-root': {
        height: '100%',
    },
}))
